/**
 * SettlementsPage — Admin Settlements Dashboard
 *
 * Calls:
 *   GET /settlements  → all settlement records
 *
 * Displays:
 *   - Summary cards (Total, Payouts, Deductions, Pending)
 *   - Settlement table: ID, sale amount, principal, interest,
 *     farmer profit, status
 *   - Waterfall detail dialog per settlement
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  Receipt,
  Search,
  FilterList,
  Visibility,
  Download,
  Refresh,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  Close,
} from '@mui/icons-material';
import { settlementAPI } from 'services/api';

// ─── Helpers ────────────────────────────────────────────────
const fmt = (amount) => {
  if (amount == null) return '₹0';
  const n = parseFloat(amount);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const fmtFull = (amount) =>
  `₹${parseFloat(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const formatDate = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ─── Status Chip ──────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cfg = {
    pending:    { label: 'Pending',    color: 'warning', icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    processing: { label: 'Processing', color: 'info',    icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    completed:  { label: 'Completed',  color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    settled:    { label: 'Settled',    color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    failed:     { label: 'Failed',     color: 'error',   icon: <Cancel sx={{ fontSize: 14 }} /> },
  };
  const c = cfg[s] || { label: status || 'Unknown', color: 'default', icon: null };
  return (
    <Chip
      icon={c.icon}
      label={c.label}
      color={c.color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.65rem', height: 24 }}
    />
  );
};

// ═════════════════════════════════════════════════════════════
const SettlementsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Waterfall dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const STATUSES = ['ALL', 'PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'];

  // ── Load Data ─────────────────────────────────────────────
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const res = await settlementAPI.getAll();
      const data = Array.isArray(res.data) ? res.data : [];
      setSettlements(data);
    } catch (err) {
      console.error('SettlementsPage load error:', err);
      setError('Failed to load settlement data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── View Detail ────────────────────────────────────────────
  const viewDetail = async (settlement) => {
    setDialogOpen(true);
    setSelectedSettlement(settlement);

    // If no transactions loaded, fetch full detail
    if (!settlement.transactions || settlement.transactions.length === 0) {
      try {
        setDetailLoading(true);
        const res = await settlementAPI.getById(settlement.id);
        setSelectedSettlement(res.data);
      } catch {
        // Use what we have
      } finally {
        setDetailLoading(false);
      }
    }
  };

  // ── Derived ────────────────────────────────────────────────
  const completedSettlements = useMemo(
    () => settlements.filter((s) => ['completed', 'settled'].includes((s.status || '').toLowerCase())),
    [settlements]
  );

  const totalGross = useMemo(
    () => completedSettlements.reduce((sum, s) => sum + (parseFloat(s.gross_amount) || 0), 0),
    [completedSettlements]
  );

  const totalDeductions = useMemo(
    () => completedSettlements.reduce((sum, s) => sum + (parseFloat(s.deductions) || 0), 0),
    [completedSettlements]
  );

  const totalNet = useMemo(
    () => completedSettlements.reduce((sum, s) => sum + (parseFloat(s.net_amount) || 0), 0),
    [completedSettlements]
  );

  const pendingCount = useMemo(
    () => settlements.filter((s) => ['pending', 'processing'].includes((s.status || '').toLowerCase())).length,
    [settlements]
  );

  const filtered = useMemo(() => {
    return settlements.filter((s) => {
      const matchSearch =
        !search ||
        (s.settlement_number || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.id?.toString() || '').includes(search) ||
        (s.harvest_id?.toString() || '').includes(search);
      const matchStatus =
        statusFilter === 'ALL' || (s.status || '').toUpperCase() === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [settlements, search, statusFilter]);

  // ── Export CSV ──────────────────────────────────────────────
  const exportCSV = () => {
    const header =
      'Settlement ID,Settlement #,Harvest ID,Date,Status,Gross Amount,Credit Repayment,Interest,Insurance Premium,Commission,Other,Total Deductions,Net (Farmer Profit)\n';
    const rows = filtered
      .map((s) =>
        [
          s.id,
          s.settlement_number,
          s.harvest_id,
          formatDate(s.settlement_date),
          s.status,
          parseFloat(s.gross_amount) || 0,
          parseFloat(s.credit_repayment) || 0,
          0, // interest (included in credit_repayment in the schema)
          parseFloat(s.insurance_premium) || 0,
          parseFloat(s.marketplace_commission) || 0,
          parseFloat(s.other_charges) || 0,
          parseFloat(s.deductions) || 0,
          parseFloat(s.net_amount) || 0,
        ].join(',')
      )
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `settlements_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Loading ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Receipt sx={{ fontSize: 22, color: '#7c3aed' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Settlements</h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <CircularProgress size={32} sx={{ color: '#7c3aed' }} />
          <span className="ml-3 text-sm text-gray-500">Loading settlement data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Receipt sx={{ fontSize: 22, color: '#7c3aed' }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Settlements</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Track settlement processing, waterfall disbursements, and farmer payouts
          </p>
        </div>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={() => loadData(true)}
            disabled={refreshing}
            sx={{ backgroundColor: '#f1f5f9', '&:hover': { backgroundColor: '#e2e8f0' } }}
          >
            <Refresh
              sx={{
                fontSize: 20,
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
              }}
            />
          </IconButton>
        </Tooltip>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} action={<Button size="small" onClick={() => loadData(true)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-2xl bg-purple-50 border border-purple-100 p-5">
          <p className="text-xs text-purple-600 font-medium mb-1">Total Settlements</p>
          <p className="text-2xl font-bold text-purple-800">{settlements.length}</p>
          <p className="text-xs text-purple-500 mt-1">{completedSettlements.length} completed</p>
        </div>
        <div className="rounded-2xl bg-blue-50 border border-blue-100 p-5">
          <p className="text-xs text-blue-600 font-medium mb-1">Total Sale Amount</p>
          <p className="text-2xl font-bold text-blue-800">{fmt(totalGross)}</p>
          <p className="text-xs text-blue-500 mt-1">Gross proceeds</p>
        </div>
        <div className="rounded-2xl bg-amber-50 border border-amber-100 p-5">
          <p className="text-xs text-amber-600 font-medium mb-1">Total Deductions</p>
          <p className="text-2xl font-bold text-amber-800">{fmt(totalDeductions)}</p>
          <p className="text-xs text-amber-500 mt-1">Principal + Interest + Premium</p>
        </div>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
          <p className="text-xs text-emerald-600 font-medium mb-1">Farmer Profit</p>
          <p className="text-2xl font-bold text-emerald-800">{fmt(totalNet)}</p>
          <p className="text-xs text-emerald-500 mt-1">
            {pendingCount > 0 ? `${pendingCount} pending` : 'All settled'}
          </p>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <TextField
              placeholder="Search by ID, settlement # or harvest..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />

            <div className="flex items-center gap-1 flex-wrap">
              <FilterList sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} />
              {STATUSES.map((s) => (
                <Chip
                  key={s}
                  label={s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
                  variant={statusFilter === s ? 'filled' : 'outlined'}
                  color={statusFilter === s ? 'primary' : 'default'}
                  size="small"
                  onClick={() => setStatusFilter(s)}
                  sx={{ fontWeight: 500, fontSize: '0.65rem', cursor: 'pointer' }}
                />
              ))}
            </div>

            <div className="ml-auto">
              <Tooltip title="Export CSV">
                <IconButton size="small" onClick={exportCSV}>
                  <Download sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Showing {filtered.length} of {settlements.length} settlements
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Settlement ID</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sale Amount</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Principal Paid</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Interest Paid</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Insurance</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Farmer Profit</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-gray-400 text-sm">
                    {settlements.length === 0
                      ? 'No settlements found. Settlements are created after harvest processing.'
                      : 'No settlements match your filters'}
                  </td>
                </tr>
              ) : (
                filtered.map((s) => {
                  // credit_repayment includes principal; interest may be in calculation_details
                  const principalPaid = parseFloat(s.credit_repayment) || 0;
                  const interestPaid =
                    s.calculation_details?.interest_deducted ||
                    s.calculation_details?.interest ||
                    0;
                  const insurancePaid = parseFloat(s.insurance_premium) || 0;
                  const farmerProfit = parseFloat(s.net_amount) || 0;

                  return (
                    <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <span className="font-mono text-xs text-gray-600">#{s.id}</span>
                          {s.settlement_number && (
                            <p className="text-[11px] text-gray-400">{s.settlement_number}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {formatDate(s.settlement_date)}
                      </td>
                      <td className="px-4 py-3 text-gray-800 font-medium">
                        {fmt(s.gross_amount)}
                      </td>
                      <td className="px-4 py-3 text-red-600 font-medium">
                        {fmt(principalPaid)}
                      </td>
                      <td className="px-4 py-3 text-orange-600 font-medium">
                        {fmt(interestPaid)}
                      </td>
                      <td className="px-4 py-3 text-amber-600 font-medium">
                        {fmt(insurancePaid)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`font-bold ${
                            farmerProfit >= 0 ? 'text-emerald-600' : 'text-red-600'
                          }`}
                        >
                          {fmtFull(farmerProfit)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusChip status={s.status} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Tooltip title="View Waterfall">
                          <IconButton size="small" onClick={() => viewDetail(s)}>
                            <Visibility sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Waterfall Detail Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <span className="text-lg font-bold">
            Settlement Waterfall
            {selectedSettlement && (
              <span className="text-sm font-normal text-gray-400 ml-2">
                #{selectedSettlement.id}
              </span>
            )}
          </span>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <CircularProgress size={28} />
            </div>
          ) : selectedSettlement ? (
            <div className="space-y-4">
              {/* Waterfall Breakdown */}
              <div className="space-y-2">
                <WaterfallRow label="Sale Proceeds (Gross)" value={selectedSettlement.gross_amount} positive />
                <WaterfallRow label="Credit Repayment (Principal)" value={selectedSettlement.credit_repayment} />
                {selectedSettlement.calculation_details?.interest_deducted > 0 && (
                  <WaterfallRow label="Interest" value={selectedSettlement.calculation_details.interest_deducted} />
                )}
                <WaterfallRow label="Insurance Premium" value={selectedSettlement.insurance_premium} />
                {parseFloat(selectedSettlement.marketplace_commission) > 0 && (
                  <WaterfallRow label="Marketplace Commission" value={selectedSettlement.marketplace_commission} />
                )}
                {parseFloat(selectedSettlement.other_charges) > 0 && (
                  <WaterfallRow label="Other Charges" value={selectedSettlement.other_charges} />
                )}
                <div className="border-t-2 border-gray-300 pt-2 mt-2">
                  <WaterfallRow
                    label="Farmer Profit (Net)"
                    value={selectedSettlement.net_amount}
                    positive
                    highlight
                  />
                </div>
              </div>

              {/* Transaction Details */}
              {selectedSettlement.transactions?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Transaction Log
                  </h4>
                  <div className="space-y-1">
                    {selectedSettlement.transactions.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-gray-50 text-xs"
                      >
                        <div>
                          <span className="font-medium text-gray-700">
                            {(t.transaction_type || '').replace(/_/g, ' ')}
                          </span>
                          {t.description && (
                            <p className="text-[11px] text-gray-400">{t.description}</p>
                          )}
                        </div>
                        <span className="font-semibold text-gray-800">{fmtFull(t.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Meta */}
              <div className="grid grid-cols-2 gap-3 text-xs mt-3 pt-3 border-t border-gray-100">
                <div>
                  <span className="text-gray-400">Settlement Date</span>
                  <p className="font-medium text-gray-700">{formatDate(selectedSettlement.settlement_date)}</p>
                </div>
                <div>
                  <span className="text-gray-400">Payment Status</span>
                  <p className="font-medium text-gray-700">
                    {(selectedSettlement.payment_status || 'N/A').replace(/_/g, ' ')}
                  </p>
                </div>
                {selectedSettlement.notes && (
                  <div className="col-span-2">
                    <span className="text-gray-400">Notes</span>
                    <p className="font-medium text-gray-700">{selectedSettlement.notes}</p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// ─── Waterfall Row ──────────────────────────────────────────
const WaterfallRow = ({ label, value, positive = false, highlight = false }) => {
  const n = parseFloat(value) || 0;
  if (n === 0 && !highlight) return null;
  return (
    <div className={`flex items-center justify-between py-1.5 px-2 rounded-lg ${highlight ? 'bg-emerald-50' : ''}`}>
      <span className={`text-sm ${highlight ? 'font-bold text-emerald-800' : 'text-gray-600'}`}>
        {label}
      </span>
      <span
        className={`text-sm font-semibold ${
          highlight
            ? n >= 0
              ? 'text-emerald-700'
              : 'text-red-600'
            : positive
              ? 'text-emerald-600'
              : 'text-red-600'
        }`}
      >
        {positive ? '' : '− '}₹{Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </span>
    </div>
  );
};

export default SettlementsPage;
